import { Box, Button, Checkbox, Dialog, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { useCallback, useEffect, useState } from 'react'
import { apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { serializeTemplateWidgets } from './widgetTemplateUtils'
import { stripWidgetSettings } from './widgetTemplateParse'

const HEADER_HEIGHT = '3.5rem'

/**
 * Full-screen editor to compose a brand-new widget template, opened from the
 * Assets "Change widget template" modal. Pressing the grid's Save asks whether
 * to only save the template (POST widgets/templates) or to also apply it to the
 * asset the modal was opened from (POST .../apply/{deviceId}).
 *
 * onClose is called with 'applied' (created + assigned to the device), 'saved'
 * (created only) or null (cancelled).
 */
const CreateTemplateDialog = ({ open, device, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [name, setName] = useState('')
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(true)
  const [choiceOpen, setChoiceOpen] = useState(false)
  const [copySettings, setCopySettings] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setWidgets([])
      setEditing(true)
      setChoiceOpen(false)
      setCopySettings(true)
    }
  }, [open])

  const reloadWidgets = useCallback(() => setWidgets([]), [])

  const onSave = useCallback(() => {
    if (!name.trim()) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    if (!Array.isArray(widgets) || widgets.length === 0) {
      showAlert({ title: t_('Add at least one widget to save the current layout as a template'), message: '', severity: 'error' })
      return
    }
    setChoiceOpen(true)
  }, [name, widgets, showAlert, t_])

  const createTemplate = () => apiPost('widgets/templates', { name: name.trim(), widgets: serializeTemplateWidgets(copySettings ? widgets : stripWidgetSettings(widgets)) })

  const handleSaveOnly = async () => {
    setChoiceOpen(false)
    setBusy(true)
    try {
      await createTemplate()
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      onClose('saved')
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleSaveAndUse = async () => {
    setChoiceOpen(false)
    setBusy(true)
    try {
      const created = await createTemplate()
      if (device && created && created.id) {
        await apiPost('widgets/templates/' + created.id + '/apply/' + device.id, {})
      }
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      onClose('applied')
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog fullScreen open={open} onClose={() => onClose(null)} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose(null)} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Create new template')}</Typography>
        <TextField
          size='small'
          label={t_('Name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          sx={{ minWidth: '16rem' }}
          inputProps={{ maxLength: 255 }}
        />
        <FormControlLabel
          control={<Checkbox checked={copySettings} onChange={(event) => setCopySettings(event.target.checked)} size='small' />}
          label={t_('Copy widget settings')}
          sx={{ whiteSpace: 'nowrap' }}
        />
        <Typography variant='caption' color='text.secondary' noWrap sx={{ flexGrow: 1, textAlign: 'right' }}>
          {device ? device.name : ''}
        </Typography>
      </Stack>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            <MosInfoProvider>
              <WidgetGrid
                widgets={widgets}
                setWidgets={setWidgets}
                reloadWidgets={reloadWidgets}
                onSave={onSave}
                editing={editing}
                setEditing={setEditing}
                showAllWidgets
                toolbarLabel={device?.name}
              />
            </MosInfoProvider>
          </Box>
        </Stack>
      </Box>

      <SmartDialog
        setOpen={choiceOpen}
        title={createDialogTitle({ color: 'primary', title: t_('Save template'), icon: SaveIcon })}
        message={t_('Assign the new template to this asset?')}
        accept={false}
        cancel
        cancelCallback={() => setChoiceOpen(false)}
        renderCustomButtons={
          <Stack direction='row' spacing={1}>
            <Button variant='outlined' onClick={handleSaveOnly} disabled={busy}>
              {t_('Save without assigning')}
            </Button>
            <Button variant='contained' onClick={handleSaveAndUse} disabled={busy || !device}>
              {t_('Save and use for this asset')}
            </Button>
          </Stack>
        }
      />
    </Dialog>
  )
}

export default CreateTemplateDialog
